import { Capacitor } from '@capacitor/core'
import OneSignal, {
  LogLevel,
  type NotificationClickEvent,
} from 'onesignal-cordova-plugin'
import { useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { ENV_IS_DEVELOPMENT, ONE_SIGNAL_APP_ID } from '../data/environment'
import { useCurrentUser } from './useCurrentUser'
import { useFeature } from './useFeatures'

/** App-relative path only — rejects absolute and protocol-relative URLs. */
export const isAppRelativePath = (value: unknown): value is string =>
  typeof value === 'string' && value.startsWith('/') && !value.startsWith('//')

/**
 * Registers the logged-in operator for OneSignal push and deep-links
 * notification taps. Native only, gated by the `notification.push` flag.
 *
 * Notifications carry `additionalData: { path: '/assignments/<id>' }` — an
 * app-relative path navigated imperatively (ADR-0003, ADR-0007).
 *
 * Must be called under `<IonReactRouter>` and `CurrentUserProvider`.
 */
export const usePushNotifications = (): void => {
  const { user } = useCurrentUser()
  const enabled = useFeature('pushNotificationsEnabled')
  const history = useHistory()
  const userId = user?.id

  useEffect(() => {
    const isReady = Capacitor.isNativePlatform() && enabled && Boolean(userId)
    if (!isReady) return

    const onClick = (event: NotificationClickEvent) => {
      const data = event.notification.additionalData as
        | Record<string, unknown>
        | undefined
      const path = data?.path
      if (isAppRelativePath(path)) {
        history.push(path)
      }
    }

    // Registered before initialize so the SDK replays a cold-start tap.
    OneSignal.Notifications.addEventListener('click', onClick)
    if (ENV_IS_DEVELOPMENT) {
      OneSignal.Debug.setLogLevel(LogLevel.Verbose)
    }
    OneSignal.initialize(ONE_SIGNAL_APP_ID)
    OneSignal.login(`${userId}`)

    const requestPermission = async () => {
      // fallbackToSettings=false: never bounce the operator to OS Settings.
      if (await OneSignal.Notifications.canRequestPermission()) {
        await OneSignal.Notifications.requestPermission(false)
      }
    }
    requestPermission().catch((error) => {
      console.error('Push permission request failed', error)
    })

    return () => {
      OneSignal.Notifications.removeEventListener('click', onClick)
      // Kill-switch: detach the alias when the flag flips off (or on session
      // change) so dispatch stops targeting this device.
      OneSignal.logout()
    }
  }, [userId, enabled, history])
}
